/**
 * useTaskSummaryGeneration Hook
 *
 * Handles streaming task summary generation with proper error handling and state management.
 * Encapsulates all API communication and streaming logic.
 */

import { useCallback } from "react";
import type { TaskSummaryData, TaskSummaryRequest } from "@/lib/types/ai";
import { parseStreamEvent } from "@/lib/utils/ai/stream-parser";

export interface UseTaskSummaryGenerationOptions {
  onContentUpdate?: (content: string) => void;
  onComplete?: (summary: TaskSummaryData) => void;
  onError?: (error: string) => void;
}

/**
 * Hook to generate task summaries with streaming support
 */
export function useTaskSummaryGeneration(options?: UseTaskSummaryGenerationOptions) {
  const generateSummary = useCallback(
    async (
      taskData: TaskSummaryRequest,
      cacheKey: string,
      onStreamingStart?: () => void,
      onStreamingEnd?: () => void
    ): Promise<TaskSummaryData> => {
      try {
        const startTime = Date.now();

        const response = await fetch("/api/ai/task-summary", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(taskData),
        });

        if (!response.ok) {
          throw new Error(`Failed to generate summary: ${response.statusText}`);
        }

        if (!response.body) {
          throw new Error("No response body");
        }

        onStreamingStart?.();

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let content = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split("\n");

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              const data = line.slice(6);
              if (data === "[DONE]") {
                onStreamingEnd?.();
                break;
              }

              try {
                const parsed = parseStreamEvent(data);
                if (parsed?.content) {
                  content += parsed.content;
                  options?.onContentUpdate?.(content);
                }
              } catch (e) {
                // Ignore parsing errors for incomplete chunks
              }
            }
          }
        }

        const processingTime = Date.now() - startTime;

        const summary: TaskSummaryData = {
          content,
          cacheKey,
          taskId: taskData.taskId,
          createdAt: new Date(),
          metadata: {
            noteCount: taskData.taskNotes.length,
            processingTime,
          },
        };

        options?.onComplete?.(summary);
        return summary;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to generate summary";
        options?.onError?.(errorMessage);
        throw err;
      }
    },
    [options]
  );

  return { generateSummary };
}
